import { useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { API, formatPrice, formatSize } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
    const { items, isOpen, setIsOpen, removeItem, updateQuantity, subtotal } = useCart();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);

    const handleCheckout = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/payments/checkout`, {
                items: items.map((i) => ({ product_id: i.id, size: i.size, quantity: i.quantity })),
                origin_url: window.location.origin,
            });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            toast.error(t("cart.error"));
            setLoading(false);
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-md bg-white flex flex-col p-0"
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-6 py-5 border-b border-zinc-200">
                    <SheetTitle className="font-display uppercase tracking-[0.2em] text-lg">
                        {t("cart.title")} ({items.length})
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center gap-3 text-zinc-400">
                            <p className="text-sm tracking-widest uppercase" data-testid="cart-empty-message">
                                {t("cart.empty")}
                            </p>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-xs underline underline-offset-4 text-black"
                                data-testid="cart-continue-shopping"
                            >
                                {t("cart.continue")}
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-zinc-100">
                            {items.map((item) => (
                                <li key={item.key} className="py-4 flex gap-4" data-testid={`cart-item-${item.key}`}>
                                    <div className="w-20 h-24 bg-zinc-100 overflow-hidden flex-shrink-0">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-semibold text-sm">{item.name}</p>
                                                <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">
                                                    {t("cart.size")} {formatSize(item.size)}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => removeItem(item.key)}
                                                aria-label="Rimuovi"
                                                data-testid={`cart-remove-${item.key}`}
                                                className="text-zinc-400 hover:text-black transition-colors"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-3 border border-zinc-200 px-2 py-1">
                                                <button
                                                    onClick={() => updateQuantity(item.key, -1)}
                                                    aria-label="Diminuisci"
                                                    data-testid={`cart-decrease-${item.key}`}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span className="text-sm w-4 text-center" data-testid={`cart-qty-${item.key}`}>
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => updateQuantity(item.key, 1)}
                                                    aria-label="Aumenta"
                                                    data-testid={`cart-increase-${item.key}`}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <p className="text-sm font-semibold">
                                                {formatPrice(item.price * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-zinc-200 px-6 py-5 space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">{t("cart.subtotal")}</span>
                            <span className="font-display font-bold text-xl" data-testid="cart-subtotal">
                                {formatPrice(subtotal)}
                            </span>
                        </div>
                        <p className="text-xs text-zinc-400">{t("cart.note")}</p>
                        <button
                            onClick={handleCheckout}
                            disabled={loading}
                            data-testid="checkout-button"
                            className="group w-full bg-black text-white py-4 text-sm tracking-[0.25em] uppercase font-semibold flex items-center justify-center gap-2 transition-colors duration-300 hover:bg-zinc-800 disabled:opacity-50"
                        >
                            {loading ? t("cart.redirecting") : t("cart.checkout")}
                            <ArrowRight
                                size={16}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
